import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { ImTv } from "react-icons/im";
import { fetchWithAuth } from "../api/fetchWithAuth";
import EraTestCaseModal from "./EraTestCaseModal";

function EraFeatureModal({ show, onClose, feature }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [showTestCaseModal, setShowTestCaseModal] = useState(false);

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  useEffect(() => {
    if (!show || !feature?.ft_number) {
      setDetails(null);
      setLoading(false);
      setSelectedTestCase(null);
      setShowTestCaseModal(false);
      return;
    }

    let cancelled = false;

    const loadDetails = async () => {
      setLoading(true);

      try {
        const res = await fetchWithAuth(`/api/era-features/${encodeURIComponent(feature.ft_number)}/details`);

        if (!res.ok) {
          if (!cancelled) {
            setDetails(null);
          }
          return;
        }

        const data = await res.json();
        if (!cancelled) {
          setDetails(data);
        }
      } catch (err) {
        if (!cancelled) {
          setDetails(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [show, feature]);

  if (!feature) return null;

  const testCases = details?.testCases ?? [];
  const openTestCase = (testCase) => {
    setSelectedTestCase(testCase);
    setShowTestCaseModal(true);
  };

  return (
    <>
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          ERA Feature {feature.ft_number}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <table className="table">
              <tbody>
                <tr style={getRowStyle(0)}>
                  <th>Number</th>
                  <td>{details?.ft_number ?? feature.ft_number}</td>
                </tr>

                <tr style={getRowStyle(1)}>
                  <th>Title</th>
                  <td>{details?.ft_title ?? feature.ft_title}</td>
                </tr>
              </tbody>
            </table>

            <div className="mt-4">
              <h6 className="mb-3">Test cases ({testCases.length})</h6>

              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.86rem" }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "80px" }}>Number</th>
                      <th>Title</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testCases.length === 0 ? (
                      <tr>
                        <td colSpan="2" className="text-center text-muted py-3">
                          No test cases found.
                        </td>
                      </tr>
                    ) : testCases.map((testCase, index) => (
                      <tr key={testCase.id} style={getRowStyle(index)}>
                        <td className="fw-semibold">
                          <button
                            type="button"
                            className="btn btn-link p-0 fw-semibold text-decoration-none"
                            onClick={() => openTestCase(testCase)}
                          >
                            {testCase.tc_number}
                          </button>
                        </td>
                        <td>
                          <div className="d-flex align-items-start gap-2">
                            {testCase.hasDmi && (
                              <span
                                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 text-primary"
                                style={{
                                  width: "22px",
                                  height: "22px",
                                  marginTop: "1px"
                                }}
                                aria-label="Contains DMI"
                                title="Contains DMI"
                              >
                                <ImTv size={13} />
                              </span>
                            )}
                            <span>{testCase.tc_title}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>

    <EraTestCaseModal
      show={showTestCaseModal}
      onClose={() => setShowTestCaseModal(false)}
      testCase={selectedTestCase}
    />
    </>
  );
}

export default EraFeatureModal;
